/**
 * Esquemas del módulo **Cobranzas** (AF §4.2, §4.4 a §4.9): membresías, cuotas, deuda,
 * congelamientos, transferencias, acuerdos de pago, débito automático Payway y checkout.
 *
 * Dos cosas que estos esquemas hacen cumplir desde el borde:
 * - **Toda excepción D5 lleva `reason`** (RN-ROL-11): condonar, congelar especial,
 *   transferir, dar de baja el débito.
 * - **Ningún importe se tipea**: lo que viaja es el concepto (`chargeIds`, `planId`,
 *   `medium`), y el precio lo resuelve la lista vigente (RN-PLA-10/11, RN-CAJ-04).
 */
import { z } from "zod";
import { CARD_BRANDS, CHARGE_KINDS, CHARGE_STATUSES, PAYWAY_CARD_METHOD_IDS, PRICE_MEDIUMS, } from "../enums.js";
import { installmentsSchema } from "./planes.js";
import { dateISO, dateOnly, moneySchema, objectId, pagination, reasonRequired, yearMonth, } from "./base.js";
// ── Membresías: venta, cambio de plan y transferencia ───────────────────────
/**
 * Venta de una membresía. **No lleva importe**: el precio sale de la lista vigente por
 * `(planId, medium, installments)` a la fecha de vencimiento (RN-PLA-11, RN-CAJ-04).
 */
export const sellMembershipBody = z
    .object({
    /**
     * El socio. Sólo puede faltar en la venta de un complemento de plataforma (RN-PLA-36):
     * ahí alcanza con el visitante, y si todavía no tiene ficha de socio se crea con sus datos.
     */
    memberId: objectId.optional(),
    planId: objectId,
    /** RN-PLA-35: la actividad a elección ilimitada, si el plan la tiene (Infinity). */
    chosenDisciplineId: objectId.nullable().optional(),
    /** RN-PLA-36: el visitante de la plataforma al que se le vende el complemento. */
    platformVisitorId: objectId.optional(),
    /**
     * RN-PLA-36: lo que la ficha de visitante no tiene y la de socio exige, para crearla a
     * partir del visitante (identidad, nombre, mail y teléfono salen del visitante).
     */
    memberFromVisitor: z
        .object({
        birthDate: dateISO,
        phone: z.string().trim().min(6).max(40).optional(),
    })
        .optional(),
    medium: z.enum(PRICE_MEDIUMS).default("cash"),
    installments: installmentsSchema.default(1),
    /** Fecha de inicio; en quincena/semana define el período fijo (RN-PLA-04). */
    startDate: dateISO.optional(),
    /** RN-PLA-16: sede y vendedor de la venta, para la comisión. */
    saleBranchId: objectId.optional(),
    sellerUserId: objectId.nullable().optional(),
    /** RN-SOC-20: el pagador puede no ser el titular ni socio. */
    payer: z
        .object({
        memberId: objectId.optional(),
        external: z
            .object({
            name: z.string().trim().min(2).max(120),
            dni: z.string().trim().min(6).max(20),
            phone: z.string().trim().max(40).default(""),
            email: z.string().trim().email().optional(),
        })
            .optional(),
    })
        .optional(),
    corporateAgreementId: objectId.nullable().optional(),
})
    .refine((body) => Boolean(body.memberId || body.platformVisitorId), {
    message: "Indicá el socio (o, en un complemento de plataforma, el visitante)",
    path: ["memberId"],
});
/**
 * RN-PLA-35: cambiar la actividad a elección de una membresía vigente. Es una excepción
 * del portal admin: lleva motivo y queda auditada (D5).
 */
export const changeChosenDisciplineBody = z.object({
    disciplineId: objectId,
    reason: reasonRequired,
});
/** RN-PLA-17: el cambio de plan se PROGRAMA y aplica al vencimiento del vigente. */
export const schedulePlanChangeBody = z.object({ planId: objectId });
/** RN-PLA-01: pack mensual de clases extra sobre el tope semanal de una disciplina. */
export const addAddonPackBody = z.object({
    disciplineId: objectId,
    perWeekExtra: z.number().int().min(1).max(7),
    /** RN-PLA-01 (flyers octubre 2026): el extra vale distinto en efectivo y con otro medio. */
    medium: z.enum(PRICE_MEDIUMS).default("cash"),
});
/**
 * RN-PLA-15: transferencia de un plan largo, **por única vez**. Es una acción del portal
 * admin con rol administradora/dueños y motivo — el flag «ya transferido» es duro.
 */
export const transferMembershipBody = z.object({
    toMemberId: objectId,
    reason: reasonRequired,
});
export const listMembershipsQuery = pagination.extend({
    memberId: objectId.optional(),
    status: z
        .enum(["active", "scheduled", "frozen", "expired", "transferred", "canceled"])
        .optional(),
    planId: objectId.optional(),
});
// ── Cuotas y deuda (RN-PLA-06..09, RN-PLA-27/28) ────────────────────────────
export const listChargesQuery = pagination.extend({
    memberId: objectId.optional(),
    membershipId: objectId.optional(),
    kind: z.enum(CHARGE_KINDS).optional(),
    status: z.enum(CHARGE_STATUSES).optional(),
    dueFrom: dateISO.optional(),
    dueTo: dateISO.optional(),
    /**
     * Fecha de la CONDONACIÓN, no del vencimiento (RN-PLA-28). «Condonaciones del período»
     * lee un período por cuándo se condonó: una cuota que venció en julio y se condonó en
     * septiembre es de septiembre, y filtrando por `dueDate` no aparecía.
     */
    forgivenFrom: dateISO.optional(),
    forgivenTo: dateISO.optional(),
});
/**
 * RN-PLA-06 / RN-PLA-12: emitir los conceptos que `whatIsOwed` proyecta con
 * `chargeId: null`. Sin `kinds` se emiten todos los que el cálculo propone.
 */
export const materializeChargesBody = z.object({
    kinds: z.array(z.enum(CHARGE_KINDS)).min(1).optional(),
});
/**
 * RN-PLA-23 — **adhesión al débito en recepción con la tarjeta tokenizada en el navegador**.
 *
 * La SPA manda la tarjeta DIRECTO a Payway (`POST {gatewayBase}/api/v2/tokens`, clave
 * pública) y acá llega sólo lo que Payway devolvió: el token de pago de 15 minutos, el BIN y
 * los últimos 4. **Ni el número ni el CVV pasan por este cuerpo**: no hay campo para ellos.
 * El importe tampoco viaja: `chargeIds` son las claves de los conceptos que la pantalla
 * mostró (`GET` del mismo recurso) y el backend recalcula cuánto se cobra (RN-CAJ-04).
 */
export const debitCardChargeBody = z.object({
    /** Token de pago de Payway (`/tokens` → `id`). Sirve para UN cobro y vence a los 15 min. */
    paymentToken: z.string().trim().min(8).max(200),
    /** Primeros 6 u 8 dígitos que devolvió la tokenización. */
    bin: z.string().regex(/^\d{6,8}$/, "El BIN son los primeros 6 u 8 dígitos"),
    paymentMethodId: z
        .number()
        .int()
        .refine((id) => PAYWAY_CARD_METHOD_IDS.includes(id), {
        message: "Medio de pago de Payway no soportado",
    }),
    last4: z.string().regex(/^\d{4}$/, "Se guardan sólo los últimos 4 dígitos (RN-PLA-23)"),
    expMonth: z.number().int().min(1).max(12),
    expYear: z.number().int().min(2020).max(2100),
    /** Las claves (`key`) de los conceptos del resumen. Se validan contra el recálculo. */
    chargeIds: z.array(z.string().trim().min(1).max(120)).min(1).max(20),
});
export const debitAdhesionBody = debitCardChargeBody.extend({
    /** Versión del `legalDocVersions.debit_consent` que firma el socio (RN-PLA-23). */
    consentVersion: z.number().int().min(1),
});
/** RN-PLA-28: **condonar una deuda sólo pueden los dueños**, con motivo, auditado (D5). */
export const forgiveChargeBody = z.object({ reason: reasonRequired });
/**
 * Pantalla «Deudores» (AF §4.9): todos los filtros que la pantalla ofrece.
 * `hasAgreement` distingue al que refinanció del que no (RN-PLA-29).
 */
export const listDebtorsQuery = pagination.extend({
    branchId: objectId.optional(),
    /** Antigüedad de la mora, en días corridos desde el vencimiento. */
    minOverdueDays: z.coerce.number().int().min(0).optional(),
    maxOverdueDays: z.coerce.number().int().min(0).optional(),
    minAmount: z.coerce.number().int().min(0).optional(),
    maxAmount: z.coerce.number().int().min(0).optional(),
    planId: objectId.optional(),
    hasAgreement: z.enum(["true", "false"]).optional(),
    /** Incluir los que están «en gestión de cobro» por reintentos de débito (RN-PLA-25). */
    includeInCollection: z.enum(["true", "false"]).optional(),
    q: z.string().trim().min(1).max(120).optional(),
});
/** Reenviar el aviso de deuda a mano desde la pantalla de Deudores. */
export const resendDebtNoticeBody = z.object({ chargeId: objectId.optional() });
// ── Acuerdos de pago (RN-PLA-29) ────────────────────────────────────────────
export const createAgreementBody = z.object({
    memberId: objectId,
    /** Los charges impagos que el acuerdo refinancia. */
    chargeIds: z.array(objectId).min(1),
    installments: z.number().int().min(2).max(24),
    /** Vencimiento de la primera cuota del acuerdo. */
    firstDueDate: dateISO,
    /** Días entre cuotas del acuerdo (default mensual). */
    everyDays: z.number().int().min(7).max(90).default(30),
    reason: reasonRequired,
});
export const listAgreementsQuery = pagination.extend({
    memberId: objectId.optional(),
    status: z.enum(["active", "completed", "defaulted"]).optional(),
});
// ── Congelamientos (RN-PLA-18/19) ───────────────────────────────────────────
/**
 * RN-PLA-19: vacaciones — sólo semestral/anual, auto-aprobado, con aviso previo.
 * El mensual NO congela por regla: pedirlo devuelve `RULE_VIOLATION` + `RN-PLA-18`.
 */
export const requestVacationFreezeBody = z.object({
    startsAt: dateISO,
    days: z.number().int().min(1).max(60),
});
/** RN-PLA-18 / RN-SOC-32: congelamiento **especial** — excepción D5, sólo dueño (PV-103). */
export const grantSpecialFreezeBody = z.object({
    membershipId: objectId,
    startsAt: dateISO,
    days: z.number().int().min(1).max(365),
    reason: reasonRequired,
});
/** Levantar un congelamiento antes de tiempo queda auditado (`lifted`). */
export const liftFreezeBody = z.object({ reason: reasonRequired });
export const listFreezesQuery = pagination.extend({
    memberId: objectId.optional(),
    membershipId: objectId.optional(),
    status: z.enum(["requested", "active", "finished", "rejected", "lifted"]).optional(),
});
// ── Débito automático Payway (RN-PLA-23..26) ────────────────────────────────
/**
 * Adhesión: llega el **token de Payway**, nunca el PAN (RN-PLA-23).
 * `consentVersion` ata la adhesión al texto legal versionado que el socio aceptó.
 */
export const createDebitAuthorizationBody = z.object({
    memberId: objectId,
    /**
     * Plan B (sin Pagos online cargado): el token de tarjeta que devolvió Payway, tipeado.
     * El PAN no pasa por acá.
     */
    paywayToken: z.string().trim().min(8).max(200),
    brand: z.enum(CARD_BRANDS),
    last4: z.string().regex(/^\d{4}$/, "Se guardan sólo los últimos 4 dígitos (RN-PLA-23)"),
    expiry: z.object({
        month: z.number().int().min(1).max(12),
        year: z.number().int().min(2020).max(2100),
    }),
    /** Versión del `legalDocVersions.debit_consent` aceptada (RN-PLA-23). */
    consentVersion: z.number().int().min(1),
    /** `signedDocuments.medium` (modelo-datos §2.3). */
    signatureMedium: z.enum(["app_authenticated", "kiosk_signature"]).default("app_authenticated"),
});
/** Re-tokenizar (tarjeta vencida o reemplazada) **no reinicia la permanencia** (RN-PLA-24). */
export const retokenizeDebitBody = z.object({
    paywayToken: z.string().trim().min(8).max(200),
    brand: z.enum(CARD_BRANDS),
    last4: z.string().regex(/^\d{4}$/),
    expiry: z.object({
        month: z.number().int().min(1).max(12),
        year: z.number().int().min(2020).max(2100),
    }),
});
/**
 * RN-PLA-26: baja del débito — **motivo obligatorio** (dato de churn) y fecha de
 * efectivización futura. La ventana de 5 días y la permanencia se validan en el servicio.
 */
export const cancelDebitBody = z.object({
    reason: reasonRequired,
    /** Fecha desde la que el socio pide que rija la baja. */
    effectiveFrom: dateISO.optional(),
    /**
     * La app confirma DESPUÉS de ver el `effectiveAt` real (fuera de ventana, la cuota en
     * curso se debita igual). Sin esto, el endpoint devuelve sólo el preview.
     */
    confirm: z.boolean().default(false),
});
export const listDebitAuthorizationsQuery = pagination.extend({
    status: z.enum(["active", "scheduled_cancel", "canceled", "suspended"]).optional(),
    /** Sólo las que todavía están dentro de la permanencia inicial (RN-PLA-24). */
    inPermanence: z.enum(["true", "false"]).optional(),
    q: z.string().trim().min(1).max(120).optional(),
});
/** Panel «Débitos automáticos»: qué ciclo se mira. */
export const debitsOverviewQuery = z.object({
    date: dateOnly.optional(),
    /** Mes para la tasa de rechazo (RN-PLA-25). */
    month: yearMonth.optional(),
});
export const listDebitAttemptsQuery = pagination.extend({
    runId: objectId.optional(),
    memberId: objectId.optional(),
    result: z.enum(["approved", "rejected", "pending"]).optional(),
    from: dateISO.optional(),
    to: dateISO.optional(),
});
/** Reintento manual de un rechazo desde el panel. */
export const manualRetryBody = z.object({ reason: z.string().trim().max(500).default("") });
/** Correr la presentación del día a mano (el job lo hace solo a las 6:00). */
export const runDebitsBody = z.object({ date: dateOnly.optional() });
// ── Pago online (RN-PLA-31/32) ──────────────────────────────────────────────
/** Checkout de la app: viajan los conceptos, nunca el importe (RN-CAJ-04). */
export const checkoutBody = z.object({
    // Cada concepto una sola vez: un id repetido duplicaría el importe de una única deuda.
    chargeIds: z
        .array(objectId)
        .min(1)
        .max(20)
        .refine((ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length, {
        message: "Cada concepto va una sola vez.",
    }),
    /** Cobrar en un acto las cuotas del grupo familiar (RN-PLA-05). */
    includeFamily: z.boolean().default(false),
    promotionId: objectId.optional(),
});
/**
 * Aviso del Link Checkout de Payway (`POST /api/payments/payway/checkout-notifications`).
 * `i` es nuestra intención (va en la `notifications_url`); el resto lo agrega Payway y se
 * acepta tal cual: el aviso no se cree, sólo se toman de él identificadores para
 * VERIFICAR con la clave privada (RN-PLA-31).
 */
export const checkoutNotificationQuery = z.looseObject({ i: objectId.optional() });
/** El cuerpo del aviso: JSON, formulario o texto. Nunca se le cree. */
export const checkoutNotificationBody = z
    .union([z.record(z.string(), z.unknown()), z.string().max(64_000)])
    .default({});
/** Vuelta de la página de pago de Payway a la app (`GET /api/public/checkout/return`). */
export const checkoutReturnQuery = z.looseObject({
    i: objectId,
    r: z.enum(["success", "cancel", "done"]).default("done"),
});
/**
 * Compra o renovación de un plan desde la app (RN-SOC-08, RN-PLA-03/13/31). **No lleva
 * importe**: el precio sale de la lista vigente por `(planId, medio, cuotas)`.
 *
 * `paymentMethod` es el medio de LISTA: sin él, débito si el socio está adherido y tarjeta
 * si no — el mismo `displayPrice` que la app le mostró en el catálogo (RN-PLA-24).
 */
export const planPurchaseBody = z.object({
    planId: objectId,
    paymentMethod: z.enum(PRICE_MEDIUMS).optional(),
    /** Sin esto, la modalidad con menos cuotas que la lista publica para ese medio. */
    installments: installmentsSchema.optional(),
    /** RN-PLA-35: la actividad a elección ilimitada, obligatoria si el plan la tiene. */
    chosenDisciplineId: objectId.optional(),
});
/**
 * Estado de deuda que ve el socio en «Mi plan» (RN-PLA-09, RN-ACC-09): al día, con una
 * cuota vencida pero todavía dentro de la tolerancia (entra con aviso), o vencida fuera de
 * la tolerancia (el molinete lo rechaza).
 */
export const APP_DEBT_STATUSES = ["up_to_date", "in_tolerance", "overdue"];
/** Nombre de cada concepto para el socio: la app no traduce `kind`, muestra esto. */
export const CHARGE_CONCEPT_LABELS = {
    membership_due: "Cuota",
    plan_installment: "Cuota",
    enrollment_fee: "Matrícula",
    class_extra: "Clase adicional",
    event_fee: "Evento",
    trial_class: "Clase de prueba",
    agreement_installment: "Cuota de plan de pagos",
};
// ── Cuenta corriente y «qué debe» ───────────────────────────────────────────
export const accountQuery = z.object({
    asOf: dateISO.optional(),
    includeFamily: z
        .union([z.boolean(), z.enum(["true", "false"])])
        .transform((v) => v === true || v === "true")
        .optional(),
});
export const whatIsOwedQuery = accountQuery.extend({
    medium: z.enum(PRICE_MEDIUMS).optional(),
});
//# sourceMappingURL=cobranzas.js.map